import { useMemo } from 'react';
import { css } from '@emotion/react';
import {
  AssociatedScheduleType_Enum,
  CallObjectUsage,
} from '@weave/schema-gen-ts/dist/schemas/phone/callroute/v1/callroute_service.pb';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { SurfaceHeader } from '@frontend/surface-header';
import { theme } from '@frontend/theme';
import {
  Chip,
  TextLink,
  Text,
  Truncated,
  Tray,
  useModalControl,
  Accordion,
  Heading,
  useTooltip,
  ChipVariants,
} from '@frontend/design-system';

type UsageDetailsProps = {
  callObjectUsage?: CallObjectUsage[];
  greetingNumber?: number;
};

export const UsageDetails = ({ callObjectUsage = [], greetingNumber }: UsageDetailsProps) => {
  const { t } = useTranslation('phone');
  const { modalProps: greetingUsageModalProps, triggerProps: greetingUsageTriggerProps } = useModalControl();

  // Sort the callObjectUsage by associatedScheduleType to show Open, Closed, Break first then the rest
  const sortedCallObjectUsage = useMemo(() => {
    const order = [
      AssociatedScheduleType_Enum.OPEN,
      AssociatedScheduleType_Enum.CLOSED,
      AssociatedScheduleType_Enum.BREAK,
      AssociatedScheduleType_Enum.OVERRIDE,
      AssociatedScheduleType_Enum.NO_HOURS,
      AssociatedScheduleType_Enum.DEVICE,
      AssociatedScheduleType_Enum.WEAVE_MANAGED,
    ];
    return [...callObjectUsage].sort(
      (a, b) =>
        order.indexOf(a.associatedScheduleType ?? AssociatedScheduleType_Enum.UNSPECIFIED) -
        order.indexOf(b.associatedScheduleType ?? AssociatedScheduleType_Enum.UNSPECIFIED)
    );
  }, [callObjectUsage]);

  if (!callObjectUsage.length) {
    return <Text size='medium'>{t('Unused')}</Text>;
  }

  const firstItem = sortedCallObjectUsage[0];
  const totalUsageCount = callObjectUsage.length;

  return (
    <>
      <div
        css={css`
          width: 100%;
          display: flex;
          gap: 16px;
          align-items: center;
        `}
      >
        <RouteUsageDisplay callObjectUsage={firstItem} useMaxWidth={totalUsageCount > 1} />
        <MoreButton
          count={totalUsageCount - 1}
          onClick={() => {
            greetingUsageTriggerProps.onClick();
          }}
        />
      </div>

      <Tray mountTarget='[data-settings-modal-content]' width='medium' {...greetingUsageModalProps}>
        <RouteHoursTray
          greetingNumber={greetingNumber}
          callObjectUsage={sortedCallObjectUsage}
          onClose={greetingUsageModalProps.onClose}
        />
      </Tray>
    </>
  );
};

const SCHEDULE_TYPE_MAP: Record<
  AssociatedScheduleType_Enum,
  { name: string; variant: ChipVariants; helperText?: string }
> = {
  [AssociatedScheduleType_Enum.UNSPECIFIED]: {
    // This shouldn't be used, but is here for completeness
    name: 'Unspecified',
    variant: 'disabled',
  },
  [AssociatedScheduleType_Enum.BREAK]: {
    name: 'Break',
    variant: 'primary',
  },
  [AssociatedScheduleType_Enum.CLOSED]: {
    name: 'Closed',
    variant: 'critical',
  },
  [AssociatedScheduleType_Enum.OPEN]: {
    name: 'Open',
    variant: 'success',
  },
  [AssociatedScheduleType_Enum.NO_HOURS]: {
    name: 'No Hours',
    variant: 'neutral',
  },
  [AssociatedScheduleType_Enum.OVERRIDE]: {
    name: 'Override',
    variant: 'neutral',
  },
  [AssociatedScheduleType_Enum.DEVICE]: {
    name: '',
    variant: 'disabled',
    helperText: 'This is the greeting assigned when this device is called by extension.',
  },
  [AssociatedScheduleType_Enum.WEAVE_MANAGED]: {
    name: 'Weave Managed',
    variant: 'disabled',
    helperText: 'The call route is currently managed by Weave Support.',
  },
};

const RouteUsageDisplay = ({
  callObjectUsage,
  useMaxWidth,
}: {
  callObjectUsage: CallObjectUsage;
  useMaxWidth: boolean;
}) => {
  const { t } = useTranslation('phone');
  const { tooltipProps: pathTooltipProps, triggerProps: pathTooltipTriggerProps, Tooltip } = useTooltip();

  const { displayName } = callObjectUsage;
  const scheduleTypeMetaData =
    SCHEDULE_TYPE_MAP[callObjectUsage?.associatedScheduleType ?? AssociatedScheduleType_Enum.UNSPECIFIED];
  const helperText = scheduleTypeMetaData?.helperText ?? '';

  const hasCallRoutePath = !!callObjectUsage?.callRoutePath?.length;

  return (
    <div
      css={css`
        display: flex;
        gap: 4px;
        align-items: center;
        max-width: 100%;
      `}
      {...pathTooltipTriggerProps}
    >
      <Truncated
        css={css`
          max-width: ${useMaxWidth ? '120px' : 'unset'};
          /* Prevent the truncated tooltip from showing up when there is a call route path available to prevent
          double tooltips from showing (the call route path will display in a tooltip also). */
          pointer-events: ${hasCallRoutePath ? 'none' : 'auto'};
        `}
      >
        {callObjectUsage.associatedScheduleType === AssociatedScheduleType_Enum.WEAVE_MANAGED
          ? t('Weave-Managed')
          : displayName}{' '}
      </Truncated>

      {(helperText || hasCallRoutePath) && (
        <Tooltip {...pathTooltipProps}>
          {!!helperText ? helperText : callObjectUsage?.callRoutePath?.join(' > ')}
        </Tooltip>
      )}

      {!!callObjectUsage &&
        !!scheduleTypeMetaData &&
        callObjectUsage.associatedScheduleType !== AssociatedScheduleType_Enum.DEVICE &&
        callObjectUsage.associatedScheduleType !== AssociatedScheduleType_Enum.WEAVE_MANAGED && (
          <>
            <Icon
              css={css`
                flex: 0 0 auto;
              `}
              color='default'
              name='caret-right-tiny'
            />

            <Chip variant={scheduleTypeMetaData.variant}>
              {callObjectUsage.associatedScheduleType === AssociatedScheduleType_Enum.BREAK
                ? callObjectUsage.associatedSchedule
                : scheduleTypeMetaData.name}
            </Chip>
          </>
        )}
    </div>
  );
};

const MoreButton = ({ count, onClick }: { count: number; onClick: () => void }) => {
  const { t } = useTranslation('phone');

  if (count <= 0) {
    return null; // No more items to show
  }

  return (
    <TextLink
      css={css`
        flex: 0 0 auto;
      `}
      onClick={onClick}
    >
      {t(`+{{count}} more`, { count })}
    </TextLink>
  );
};

type GroupedUsage = Omit<CallObjectUsage, 'callRoutePath'> & {
  paths: string[][];
};
const groupPathsByIdAndSchedule = (callObjectUsage: CallObjectUsage[]) => {
  // We need to group the items by their instructionSetId and associatedScheduleType and associatedSchedule.
  return callObjectUsage.reduce((acc, item) => {
    const key = `${item.instructionSetId}-${item.associatedScheduleType}-${item.associatedSchedule}`;
    if (!acc[key]) {
      const { callRoutePath, ...rest } = item;
      acc[key] = {
        ...rest,
        paths: [],
      };
    }

    if (item.callRoutePath) {
      acc[key].paths.push(item.callRoutePath);
    }
    return acc;
  }, {} as Record<string, GroupedUsage>);
};

const RouteHoursTray = ({
  greetingNumber,
  callObjectUsage,
  onClose,
}: {
  greetingNumber?: number;
  callObjectUsage: CallObjectUsage[];
  onClose: () => void;
}) => {
  const { t } = useTranslation('phone');

  const processedUsage = useMemo(() => {
    // Process the callObjectUsage to get a flat list of usage items with their displayName
    return groupPathsByIdAndSchedule(callObjectUsage);
  }, [callObjectUsage]);

  return (
    <>
      <SurfaceHeader.Root noBorder css={{ margin: theme.spacing(-4, -4, 0, -4), width: 'calc(100% + 64px)' }}>
        <SurfaceHeader.Row>
          <SurfaceHeader.Title>{t('Greeting Usage')}</SurfaceHeader.Title>
          <SurfaceHeader.Actions>
            <SurfaceHeader.Icon
              iconName='x'
              onClick={() => {
                onClose();
              }}
            />
          </SurfaceHeader.Actions>
        </SurfaceHeader.Row>
        <SurfaceHeader.Body>
          {t('Greeting {{greetingNumber}}', { greetingNumber: greetingNumber?.toString().padStart(2, '0') }) ??
            t('Unknown Greeting Number')}
        </SurfaceHeader.Body>
      </SurfaceHeader.Root>
      <Text
        size='large'
        weight='bold'
        css={css`
          border-bottom: 1px solid ${theme.colors.neutral20};
          padding-bottom: ${theme.spacing(2)};
        `}
      >
        {t('Route and Hours')}
      </Text>
      <Tray.Body>
        {Object.values(processedUsage).map((item, index) => {
          const scheduleTypeMetaData =
            SCHEDULE_TYPE_MAP[item.associatedScheduleType ?? AssociatedScheduleType_Enum.UNSPECIFIED];
          const scheduleType = scheduleTypeMetaData?.name ?? '';
          const helperText = scheduleTypeMetaData?.helperText ?? '';

          return (
            <Accordion
              key={(item?.displayName ?? '') + index}
              css={css`
                > div {
                  box-shadow: none;
                  border: 1px solid ${theme.colors.neutral20};
                }

                > div h3 button section div svg {
                  fill: ${theme.colors.neutral70};
                }

                div[data-active='true'] h3 button {
                  background-color: ${theme.colors.neutral5};
                  border-bottom: 1px solid ${theme.colors.neutral10};
                }
              `}
            >
              <Accordion.Item value={'greetingUsage'}>
                <Accordion.Header>
                  {!!scheduleType && !!item.paths.flat().length ? (
                    <Heading level={3}>{`${item.displayName} | ${
                      item.associatedScheduleType === AssociatedScheduleType_Enum.BREAK
                        ? item.associatedSchedule
                        : scheduleType
                    }`}</Heading>
                  ) : (
                    <Heading level={3}>{item.displayName}</Heading>
                  )}
                </Accordion.Header>
                <Accordion.Body
                  css={css`
                    display: flex;
                    flex-direction: column;
                    gap: ${theme.spacing(1)};
                  `}
                >
                  {!!item.paths?.length ? (
                    item.paths?.map((path, usageIndex) => (
                      <div key={usageIndex}>
                        {!!path?.length ? <Text>{path.join(' > ')} </Text> : <Text>{helperText}</Text>}
                      </div>
                    ))
                  ) : (
                    <Text>{t('No route')}</Text>
                  )}
                </Accordion.Body>
              </Accordion.Item>
            </Accordion>
          );
        })}
      </Tray.Body>
    </>
  );
};
